/**
 * Admin API client.
 */

import { apiFetch } from "./client";
import type {
  AdminMe,
  BulkUploadResponse,
  CertificateOut,
  CertificateUpdate,
  EventCreate,
  EventOut,
  EventUpdate,
  LoginRequest,
  LoginResponse,
} from "./types";

// ─── Auth ──────────────────────────────────────────────────────────────────

export async function login(req: LoginRequest): Promise<LoginResponse> {
  return apiFetch<LoginResponse>("/admin/login", {
    method: "POST",
    body: req,
    auth: false,
  });
}

export async function getMe(): Promise<AdminMe> {
  return apiFetch<AdminMe>("/admin/me");
}

// ─── Events CRUD ───────────────────────────────────────────────────────────

export async function listEvents(): Promise<EventOut[]> {
  return apiFetch<EventOut[]>("/admin/events");
}

export async function getEvent(id: string): Promise<EventOut> {
  return apiFetch<EventOut>(`/admin/events/${id}`);
}

export async function createEvent(data: EventCreate): Promise<EventOut> {
  return apiFetch<EventOut>("/admin/events", { method: "POST", body: data });
}

export async function updateEvent(id: string, data: EventUpdate): Promise<EventOut> {
  return apiFetch<EventOut>(`/admin/events/${id}`, { method: "PATCH", body: data });
}

export async function deleteEvent(id: string, opts: { cascade?: boolean } = {}): Promise<void> {
  const qs = opts.cascade ? "?cascade=true" : "";
  return apiFetch<void>(`/admin/events/${id}${qs}`, { method: "DELETE" });
}

// ─── Certificates CRUD ─────────────────────────────────────────────────────

export async function listCertificates(eventId: string): Promise<CertificateOut[]> {
  return apiFetch<CertificateOut[]>(`/admin/events/${eventId}/certificates`);
}

export async function uploadCertificate(
  eventId: string,
  holderName: string,
  file: File,
): Promise<CertificateOut> {
  const fd = new FormData();
  fd.append("holder_name", holderName);
  fd.append("file", file);
  return apiFetch<CertificateOut>(`/admin/events/${eventId}/certificates`, {
    method: "POST",
    body: fd,
  });
}

export async function bulkUpload(eventId: string, archive: File): Promise<BulkUploadResponse> {
  const fd = new FormData();
  fd.append("archive", archive);
  return apiFetch<BulkUploadResponse>(`/admin/events/${eventId}/certificates/bulk`, {
    method: "POST",
    body: fd,
  });
}

export async function updateCertificate(
  id: string,
  data: CertificateUpdate,
): Promise<CertificateOut> {
  return apiFetch<CertificateOut>(`/admin/certificates/${id}`, {
    method: "PATCH",
    body: data,
  });
}

export async function deleteCertificate(id: string): Promise<void> {
  return apiFetch<void>(`/admin/certificates/${id}`, { method: "DELETE" });
}

/** Builds the admin-preview URL (no token). The browser fetches it directly. */
export function adminPdfUrl(certificateId: string): string {
  return `/api/admin/certificates/${certificateId}/pdf`;
}
